<template>
    <div class="filter-min-max">
        <div class="filter-label" @click="showOptions = !showOptions">
            {{ valueLabel }}
            <span v-if="periodLabel" class="period-label">
                {{ t('reportFilters-period_days_ago', [periodLabel]) }}
            </span>
        </div>
        <div
            class="filter-options"
            v-if="showOptions"
            v-click-outside="clickedOutsideOptions"
        >
            <div class="fields">
                <div class="field">
                    <label :for="`filter-${name}-min`">{{ t('reportFilters-min') }}:</label>
                    <input type="tel" v-model="min" v-mask="options.mask" :name="`${name}-min`" :id="`filter-${name}-min`" />
                </div>
                <div class="field">
                    <label :for="`filter-${name}-max`">{{ t('reportFilters-max') }}:</label>
                    <input type="tel" v-model="max" v-mask="options.mask" :name="`${name}-max`" :id="`filter-${name}-max`" />
                </div>

                <div class="field">
                    <label class="checkbox">
                        <input
                            type="checkbox"
                            v-model="restrictPeriod"
                            :id="`filter-${name}-restrict`"
                            :name="name"
                        /> {{ t('reportFilters-restrict_time_range') }}
                    </label>
                </div>

                <template v-if="restrictPeriod">
                    <div class="field">
                        <div class="period-title">{{ t('reportFilters-days_ago_title') }}</div>
                    </div>
                    <div class="field">
                        <label :for="`filter-${name}-period-min`">{{ t('reportFilters-min') }}:</label>
                        <input type="tel" v-model="period.min" :id="`filter-${name}-period-min`" :name="name" />
                    </div>
                    <div class="field">
                        <label :for="`filter-${name}-period-max`">{{ t('reportFilters-max') }}:</label>
                        <input type="tel" v-model="period.max" :id="`filter-${name}-period-max`" :name="name" />
                    </div>
                </template>
            </div>
            <div class="filter-actions">
                <button
                    type="button"
                    class="cancelBtn btn btn-sm btn-default"
                    @click="cancel"
                >
                    {{ t('reportFilters-close') }}
                </button>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.field {
    display: flex;
    label {
        width: auto;
        display: block;
        margin-right: 10px;
    }
}

.period-label {
    display: block;
    font-size: 8px;
}
.filter-actions {
    margin-top: 20px;
}

label.checkbox {
    font-size: 10px;

    input {
        margin-right: 5px;
    }
}

.period-title {
    font-size: 12px;
    font-weight: 600;
    margin: 10px 0 0 10px;
}

input:not([type="checkbox"]) {
    border: none;
    border-bottom: 1px solid #ddd;
    box-shadow: none;
    font-size: 13px;
    flex-grow: 1;
    width: 100%;
    &:focus {
        outline: none;
        box-shadow: none;
        border: none;
        border-bottom: 1px solid #ddd;
    }
}
</style>
<script>
export default {
    name: "min-max",
    props: {
        options: {
            type: Object,
            default: () => ({
                mask: "R$ ######,##",
                optionsWidth: 300,
            }),
        },
        name: {
            type: String,
            default: "min-max",
        },
        currentValue: {
            type: Object,
            default: () => ({
                min: "",
                max: "",
                restrictPeriod: false,
                period: {
                    min: null,
                    max: null,
                },
            }),
        },
    },
    data() {
        return {
            showOptions: false,
            clickedOutsideOptions: () => {},
            value: "",
            changedValue: false,
            min: null,
            max: null,
            period: {
                min: null,
                max: null,
            },
            restrictPeriod: false,
        };
    },
    mounted() {
        if (this.currentValue) {
            let value = this.currentValue;
            this.max = value.max;
            this.min = value.min;
            this.restrictPeriod = value.restrictPeriod;
            this.period.min = value.period.min;
            this.period.max = value.period.max;
        }
        if (this.value.length > 0) {
            this.changed();
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        cancel() {
            this.showOptions = false;
        },
        changed() {
            this.$emit("changed", this.value);
        },
        checkValuesOrder() {
            if (this.max && this.maxFloat < this.minFloat) {
                let max = this.max;
                let min = this.min;
                this.max = min;
                this.min = max;
            }

            if (
                this.period.max &&
                this.getFloat(this.period.max) < this.getFloat(this.period.min)
            ) {
                let periodMax = this.period.max;
                let periodMin = this.period.min;
                this.period.max = periodMin;
                this.period.min = periodMax;
            }
        },
        setValue() {
            this.checkValuesOrder();
            this.value = {
                min: this.minFloat,
                max: this.maxFloat,
                restrictPeriod: this.restrictPeriod ? 1 : "",
                period: {
                    min: this.getFloat(this.period.min),
                    max: this.getFloat(this.period.max),
                },
            };
        },
        getFloat(n) {
            if (n === null) {
                return null;
            }
            return parseFloat(
                String(n)
                    .replace(/[^0-9,]/gi, "")
                    .replace(",", "."),
            );
        },
    },
    computed: {
        maxFloat() {
            return this.getFloat(this.max);
        },
        minFloat() {
            return this.getFloat(this.min);
        },
        valueLabel() {
            const isEmpty = (v) => v === null || v === "" || v === undefined;
            if (isEmpty(this.min) && isEmpty(this.max)) {
                return this.t('reportFilters-enter_values');
            } else if (isEmpty(this.min) && !isEmpty(this.max)) {
                return this.t('reportFilters-up_to', [this.max]);
            } else if (!isEmpty(this.min) && isEmpty(this.max)) {
                return this.t('reportFilters-from', [this.min]);
            } else {
                return this.t('reportFilters-between', [this.min, this.max]);
            }
        },
        periodLabel() {
            if (!this.restrictPeriod) {
                return "";
            }
            if (!this.period.min && !this.period.max) {
                return "";
            } else if (!this.period.min && this.period.max) {
                return this.t('reportFilters-up_to', [this.period.max]);
            } else if (!this.period.max && this.period.min) {
                return this.t('reportFilters-more_than', [this.period.min]);
            } else {
                return this.t('reportFilters-between', [this.period.min, this.period.max]);
            }
        },
    },
    watch: {
        showOptions() {
            this.setValue();
            if (this.showOptions) {
                window.setTimeout(() => {
                    this.clickedOutsideOptions = () => {
                        if (this.showOptions) {
                            this.cancel();
                        }
                    };
                }, 100);
            } else {
                this.clickedOutsideOptions = () => {};
            }
        },
        value() {
            this.changed();
        },
    },
};
</script>
